/**
 * AB#21245 — a semantic tone for each order status.
 *
 * The status cell was a filled lavender box on nearly every row. One low-contrast
 * fill for nineteen different states meant the column could be read but not
 * *scanned*: "Mark to Factory" and "Customer Rejected" looked equally calm, and
 * the grey-on-grey of the most common state read as disabled.
 *
 * Five tones, chosen by who is blocked and whether anything is wrong — not by
 * where the status sits in the workflow. A CSM scanning this column is asking
 * "which of these needs me today", and the answer is the tone, not the stage.
 *
 * Semantic colour, deliberately separate from the brand accent: an order being
 * healthy is not the same kind of statement as a button being primary.
 */

export type StatusTone = 'todo' | 'progress' | 'good' | 'waiting' | 'bad';

const TONES: Record<string, StatusTone> = {
  // On our desk — someone here has to act.
  order_approve: 'todo',
  pending_for_factory_assignment: 'todo',
  quote_requested: 'todo',

  // Moving under its own steam.
  in_production: 'progress',
  production_files_uploaded: 'progress',
  shipped: 'progress',

  // Cleared a gate.
  factory_approved: 'good',
  customer_approved: 'good',
  completed: 'good',

  // Waiting on somebody else. Not a problem yet, but not progress either.
  submitted_for_factory_review: 'waiting',
  submitted_for_customer_review: 'waiting',
  quote_provided: 'waiting',
  awaiting_consolidation: 'waiting',
  on_hold: 'waiting',
  delivery_request_date: 'waiting',

  // Something went wrong and a person has to unpick it.
  factory_rejected: 'bad',
  customer_rejected: 'bad',
  quote_rejected: 'bad',
  order_cancel: 'bad',
};

/**
 * The tone for a status key.
 *
 * An unknown status falls to `waiting` rather than to a happy or an alarming
 * tone: a state this map has not been taught about should not be able to claim
 * an order is fine, and should not raise a false alarm either. New statuses get
 * added to four enum sources at once in this codebase, so one arriving here
 * unmapped is a question of when, not if.
 */
export const statusTone = (status: string | null | undefined): StatusTone =>
  (status && TONES[status]) || 'waiting';

/**
 * Every status this map knows, so a test can hold it against the label enum.
 *
 * Note that `CustimooOrderFlowStatuses` carries two kinds of key: snake_case
 * ones whose value is a human label, and SCREAMING_CASE aliases whose value is
 * a snake_case status. Only the first kind is a status.
 */
export const mappedStatuses = (): string[] => Object.keys(TONES);

/** Dot colours. Semantic, and deliberately not the brand accent. */
export const TONE_DOT: Record<StatusTone, string> = {
  todo: '#CE7415',
  progress: '#1F73B7',
  good: '#1F8A5B',
  waiting: '#8E8E93',
  bad: '#C62828',
};

/** Label colours, darkened from the dot so the text clears 4.5:1 on white. */
export const TONE_TEXT: Record<StatusTone, string> = {
  todo: '#8A5219',
  progress: '#17558A',
  good: '#156647',
  waiting: '#5E5E63',
  bad: '#B00020',
};
